import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
    selectPrimaryColor,
    selectTheme,
    setPrimaryColor,
    toggleTheme,
} from '@/store/slices/uiSlice';
import { Moon, Palette, Sun } from 'lucide-react';

const PRIMARY_SWATCHES = [
    { key: 'white', hex: '#ffffff' },
    { key: 'green', hex: '#10b981' },
    { key: 'yellow', hex: '#eab308' },
    { key: 'orange', hex: '#f97316' },
    { key: 'blue', hex: '#2563eb' },
    { key: 'cyan', hex: '#06b6d4' },
    { key: 'purple', hex: '#7c3aed' },
    { key: 'pink', hex: '#ec4899' },
    { key: 'red', hex: '#ef4444' },
];

function cx(...classes) {
    return classes.filter(Boolean).join(' ');
}

function CircleButton({ color, selected, onClick, ariaLabel }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={ariaLabel}
            className={cx(
                'h-9 w-9 rounded-full border border-border/40 transition',
                selected ? 'ring-2 ring-primary-500 border-primary-500/40' : 'hover:brightness-110',
            )}
            style={{ backgroundColor: color }}
        />
    );
}

function useOutsideCloseAndEscape(ref, open, onClose) {
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onClose();
        };
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [ref, open, onClose]);
}

export function ThemePaletteMenuRow({ onOpenPanel }) {
    return (
        <button
            type="button"
            onClick={onOpenPanel}
            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
            <Palette className="h-3.5 w-3.5" aria-hidden />
            Theme
        </button>
    );
}

export function ThemePalettePanel({ open, onClose, anchor = 'dropdown' }) {
    const dispatch = useAppDispatch();
    const primaryColor = useAppSelector(selectPrimaryColor);
    const theme = useAppSelector(selectTheme);
    const isDark = theme === 'dark';

    const ref = useRef(null);
    useOutsideCloseAndEscape(ref, open, onClose);

    const panelBgClass = 'bg-slate-900/60 backdrop-blur border border-border/30';

    if (!open) return null;

    const positionClass =
        anchor === 'fixed'
            ? 'fixed inset-x-4 top-20 z-[100] mx-auto max-w-[22rem]'
            : 'absolute right-0 top-full z-[90] mt-2 w-[22rem] max-w-[calc(100vw-2rem)]';

    return (
        <div
            ref={ref}
            className={cx(positionClass, 'rounded-xl p-4 shadow-xl', panelBgClass)}
            role="dialog"
            aria-label="Theme palette"
        >
            <div className="mb-4 flex items-center justify-between gap-3 border-b border-white/10 pb-3">
                <span className="text-sm font-semibold tracking-wide text-primary-200">Mode</span>
                <button
                    type="button"
                    onClick={() => dispatch(toggleTheme())}
                    className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-200 transition hover:bg-white/10 active:bg-white/15"
                    aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
                    title={isDark ? 'Light mode' : 'Dark mode'}
                >
                    {isDark ? (
                        <Sun className="h-4 w-4 shrink-0" aria-hidden />
                    ) : (
                        <Moon className="h-4 w-4 shrink-0" aria-hidden />
                    )}
                    <span>{isDark ? 'Light' : 'Dark'}</span>
                </button>
            </div>

            <PanelSection title="Primary">
                <div className="grid grid-cols-4 gap-3 md:grid-cols-9">
                    {PRIMARY_SWATCHES.map((s) => (
                        <CircleButton
                            key={s.key}
                            color={s.hex}
                            selected={s.key === primaryColor}
                            onClick={() => dispatch(setPrimaryColor(s.key))}
                            ariaLabel={`Primary ${s.key}`}
                        />
                    ))}
                </div>
            </PanelSection>
        </div>
    );
}

export function ThemePalettePicker() {
    const [open, setOpen] = useState(false);

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
                aria-label="Open theme palette"
                title="Theme palette"
            >
                <Palette className="h-6 w-6" aria-hidden />
            </button>

            <ThemePalettePanel open={open} onClose={() => setOpen(false)} anchor="dropdown" />
        </div>
    );
}

function PanelSection({ title, children }) {
    return (
        <div>
            <div className="mb-2 text-sm font-semibold tracking-wide text-primary-200">{title}</div>
            {children}
        </div>
    );
}

export default ThemePalettePicker;

